import { QueryClient, dehydrate, hydrate } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";

// Stale-chunk recovery. After a redeploy, an already-open tab still
// references the previous build's hashed route chunks. The next in-app
// navigation triggers a dynamic import() for a filename that no longer
// exists on the CDN — Vite raises `vite:preloadError`, TanStack Router
// otherwise surfaces our notFoundComponent for a URL that is perfectly
// valid. Force a full reload so the browser fetches the current HTML
// shell (and current chunk manifest), then the user lands on the page
// they meant to visit.
//
// Guard against reload loops: only reload once per session per URL.
if (typeof window !== "undefined") {
  const RELOAD_FLAG = "__mo_chunk_reload__";
  const handleStaleChunk = (event?: Event) => {
    event?.preventDefault?.();
    try {
      const key = `${RELOAD_FLAG}:${window.location.pathname}${window.location.search}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch {
      // sessionStorage unavailable (private mode / storage full) — reload anyway.
    }
    window.location.reload();
  };
  window.addEventListener("vite:preloadError", handleStaleChunk as EventListener);
  // Belt & braces: any uncaught chunk-load error from React.lazy or manual
  // import() that Vite didn't tag as a preload error.
  window.addEventListener("error", (event) => {
    const msg = String((event as ErrorEvent)?.message ?? "");
    if (/Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(msg)) {
      handleStaleChunk(event);
    }
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = (event as PromiseRejectionEvent)?.reason;
    const msg = String(reason?.message ?? reason ?? "");
    if (/Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(msg)) {
      handleStaleChunk(event as unknown as Event);
    }
  });
}

export const getRouter = () => {
  const queryClient = new QueryClient();

  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
    // Ship the server-side TanStack Query cache through the SSR payload so
    // the client picks up whatever the loader already fetched instead of
    // re-running `useSuspenseQuery` on hydration. Prevents the class of
    // headless-browser scrape (Product Hunt, LinkedIn preview bots) that
    // caught the page mid-refetch and screenshotted the empty state.
    dehydrate: (() => ({
      queryClientState: dehydrate(queryClient),
    })) as any,
    hydrate: ((data: { queryClientState: ReturnType<typeof dehydrate> }) => {
      hydrate(queryClient, data.queryClientState);
    }) as any,


  });

  return router;
};
